import { format } from 'date-fns'
import type { FieldConfig as FormikFieldConfig } from 'formik/dist/Field'
import { t } from 'i18next'
import { useContext, useRef } from 'react'

import { AuthSessionContext } from '@app/context/AuthContext'

import { Input } from '@ui/components/Input'
import { CalendarIcon } from '@ui/icons/Calendar.icon'
import { CalendarPopup } from '@ui/v2/Calendar/CalendarPopup'

import { API_DATE_FORMAT } from '@utils/date.utils'

import { Box } from '@shared/components/Box'

import { getLabel, getPlaceholder } from '../formField/helpers'
import type { TypedFieldProps } from '../types/TypedFieldProps'

import styles from './DatePicker.module.scss'

export type DatePickerValue = Date | string | undefined | null
type FieldConfig = Omit<FormikFieldConfig, 'name'>
interface DatePickerProps extends FieldConfig {
  maxDate?: Date
  showYearDropdown?: boolean
  value?: DatePickerValue
  hint?: string
  onChange?(value: Date | null): void
  onClick?(): void
  onBlur?(): void
  error?: string
  minDate?: Date
  placement?: 'top' | 'bottom'
  disableYearMonthMode?: boolean
  fullWidth?: boolean
}

export function DatePicker<FormValues>(props: DatePickerProps & TypedFieldProps<FormValues>) {
  const {
    hint,
    type = 'text',
    required,
    disabled,
    minDate,
    maxDate,
    onChange = () => {},
    showYearDropdown = true,
    error,
    onBlur,
    placement = 'bottom',
    disableYearMonthMode,
    size,
    fullWidth,
  } = props

  const fieldRef = useRef<HTMLDivElement>(null)

  const value = getDateValue(props.value)
  const placeholder = getPlaceholder(props)
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name ?? API_DATE_FORMAT
  const label = getLabel(props) ?? t('Format - {{dateFormat}}', { dateFormat })

  return (
    <Box fullWidth={fullWidth} ref={fieldRef}>
      {disabled ? (
        <Input
          value={value ? format(new Date(value), dateFormat) : ''}
          type={type}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          rightIcon={CalendarIcon}
          label={label}
          error={!!error}
          hint={error || hint}
          onBlur={onBlur}
          size={size}
          fullWidth={fullWidth}
        />
      ) : (
        <CalendarPopup
          selectsRange={false}
          selectedDate={value ? new Date(value) : undefined}
          onConfirm={(date: Date | null) => onChange(date ?? null)}
          minDate={minDate}
          maxDate={maxDate}
          label={label}
          showMonthYearPicker
          showYearPicker
          showYearDropdown={showYearDropdown}
          popupWrapperClassName={styles.datePickerWrapper}
          placement={placement}
          disableYearMonthMode={disableYearMonthMode}
        >
          <Input
            value={value ? format(new Date(value), 'dd/MM/yyyy') : ''}
            type={type}
            required={required}
            disabled={false}
            placeholder={placeholder}
            rightIcon={CalendarIcon}
            label={label}
            error={!!error}
            hint={error || hint}
            onBlur={onBlur}
            size={size}
            fullWidth={fullWidth}
          />
        </CalendarPopup>
      )}
    </Box>
  )
}

const getDateValue = (date: DatePickerValue) => {
  if (date instanceof Date) {
    return date.toISOString()
  }

  if (typeof date === 'string') {
    return date
  }

  return undefined
}
